import React from 'react'
import { toaster } from '../utils/toaster'
import { TOAST_TIMEOUT } from 'src/constants/resurrection-constants'
import { History } from 'history'

// Define interfaces for type safety
export interface ResurrectionCampaign {
  id: string | null
  chefId: string | null
  chefBrandId: string | null
  highlightedMealsIds: string[]
  couponCode: string | null
  prepaidCampaign: string | null
  chefBrandIds: string[] | null
}

/**
 * Removes empty or null values from an object
 * @param obj Object to clean
 * @returns Object without empty/null values
 */
export function removeEmpty(obj: Record<string, any>): Record<string, any> {
  return Object.entries(obj).reduce(
    (acc: Record<string, any>, [key, value]) => {
      if (value !== null && value !== undefined) {
        acc[key] = value
      }
      return acc
    },
    {}
  )
}

/**
 * Builds a URL with query parameters
 * @param route Base route
 * @param params Query parameters
 * @returns Full URL with query string
 */
export function getRedirectUrl(
  route: string,
  params: Record<string, any>
): string {
  const cleanParams = removeEmpty(params)
  const query = new URLSearchParams(cleanParams).toString()
  return `${route}?${query}`
}

/**
 * Prepare structured resurrection campaign data from payload
 * @param campaign The resurrection campaign from payload
 * @returns Normalized resurrection campaign data
 */
export function prepareResurrectionCampaign(
  campaign?: ResurrectionCampaign
): ResurrectionCampaign | null {
  if (!campaign) {
    return null
  }

  return {
    id: campaign.id ?? null,
    chefId: campaign.chefId ?? null,
    chefBrandId: campaign.chefBrandId ?? null,
    highlightedMealsIds: campaign.highlightedMealsIds ?? [],
    couponCode: campaign.couponCode ?? null,
    prepaidCampaign: campaign.prepaidCampaign ?? null,
    chefBrandIds: campaign.chefBrandIds ?? null
  }
}

/**
 * Handle navigation after successful reactivation
 * @param history History object for navigation
 * @param redirectToDeliveryDate Optional delivery date to redirect to
 * @param options Additional options for navigation
 */
export function handleReactivationRedirect(
  history: History,
  redirectToDeliveryDate?: string,
  options?: {
    chefIds?: string[]
    mealIds?: string | null
    reloadSubscription?: () => void
    redirectRoute?: string
    delay?: number
  }
): void {
  const {
    chefIds,
    mealIds,
    reloadSubscription,
    redirectRoute = '/validate-cc',
    delay = TOAST_TIMEOUT.STANDARD
  } = options ?? {}

  // Allow time for user to read toast message before redirecting
  setTimeout(() => {
    // Navigate to the appropriate route
    if (redirectToDeliveryDate) {
      const url = getRedirectUrl('/meals', {
        date: redirectToDeliveryDate,
        chefIds,
        mealIds
      })
      window.location.href = url // history.push wasn't working as expected due to sagas
    } else {
      history.replace(redirectRoute)
      if (reloadSubscription) {
        reloadSubscription()
      }
    }
  }, delay)
}

/**
 * Shows a success message for successful reactivation
 */
export function showReactivationSuccessMessage(): void {
  toaster.success(
    React.createElement(
      'span',
      null,
      'Your subscription was successfully ',
      React.createElement('b', null, 'reactivated'),
      ". See what's new and order your next delivery!"
    ),
    { autoClose: TOAST_TIMEOUT.STANDARD }
  )
}

/**
 * Shows a success message for successful reactivation and order placement in the new resurrection flow
 */
export function showReactivationAndOrderSuccessMessage(): void {
  toaster.success(
    React.createElement(
      'span',
      null,
      'Your subscription was successfully reactivated and your order was placed, welcome back to CookUnity!'
    ),
    { autoClose: TOAST_TIMEOUT.STANDARD } // Or a longer timeout if desired
  )
}

/**
 * Shows a success message for prepaid offer application
 */
export function showPrepaidOfferSuccessMessage(): void {
  toaster.success(
    React.createElement(
      'span',
      null,
      'Your prepaid offer was successfully applied to your subscription.'
    ),
    { autoClose: TOAST_TIMEOUT.STANDARD }
  )
}

/**
 * Shows an error message for prepaid offer application failure
 */
export function showPrepaidOfferErrorMessage(): void {
  toaster.error(
    React.createElement(
      'span',
      null,
      'There was an error with your prepaid offer. Please contact support.'
    ),
    { autoClose: TOAST_TIMEOUT.STANDARD }
  )
}

/**
 * Shows an error message for coupon application failure
 * @param couponCode The coupon code that failed
 */
export function showCouponErrorMessage(couponCode: string): void {
  toaster.error(
    React.createElement(
      'span',
      null,
      `Could not apply coupon code "${couponCode}". Please contact support.`
    ),
    { autoClose: TOAST_TIMEOUT.STANDARD }
  )
}

/**
 * Shows an error message for cart generation failure
 */
export function showCartGenerationErrorMessage(): void {
  toaster.error(
    React.createElement(
      'span',
      null,
      'There was an error preparing your meals. Please try again or contact support.'
    ),
    { autoClose: TOAST_TIMEOUT.STANDARD }
  )
}

/**
 * Shows an error message for reactivation failure
 * @param error The error object or message
 */
export function showReactivationErrorMessage(error: any): void {
  const errorMessage = error?.message ?? error?.toString() ?? 'Unknown error'

  // Log the full error for debugging
  console.error('Reactivation error:', error)

  toaster.error(
    React.createElement(
      'span',
      null,
      "We couldn't reactivate your subscription. Please try again or contact support. ",
      React.createElement('small', null, errorMessage)
    ),
    { autoClose: TOAST_TIMEOUT.STANDARD }
  )
}
